// File: src/utils/deepLink.js
/**
 * Deep-link support for the OSHA Code Viewer.
 *
 * The viewer is a state-driven SPA served statically, so shareable URLs use
 * query parameters (no server-side rewrites needed on any static host):
 *
 *   /?reg=1926                     browse a regulation part
 *   /?section=1910.1200            open a specific section
 *   /?section=1910.1200&lang=es    open it in Spanish
 *   /?search=fall%20protection     open search pre-filled
 *
 * Sibling apps (cdrisk.com, the CDRisk employee/inspector apps, sds.guide)
 * link in with these URLs, so the parameter names are a published contract —
 * change them only with a redirect for the old form.
 */

// Part codes that have served data (public/data/<code>/).
export const VALID_REGULATION_CODES = [
  '1902', '1903', '1904', '1905', '1910', '1915',
  '1917', '1918', '1919', '1926', '1928',
];

const VALID_LANGS = ['en', 'es', 'ar'];

// Section codes range from "1910.22" to "1910.SubpartT.AppC" and
// "1910.1451-1910.1499" — allow word chars, dots and hyphens only.
const SECTION_RE = /^\d{4}[\w.-]*$/;

/**
 * Extract a viewer section code from a citation string such as
 * "29 CFR 1910.1200(g)" or "1926.501(b)(1)". Returns null when the string
 * does not contain a section of a part this viewer serves.
 */
export function sectionFromCitation(citation) {
  if (!citation) return null;
  const m = String(citation).match(/\b(\d{4})\.(\d+[a-zA-Z]*)/);
  if (!m || !VALID_REGULATION_CODES.includes(m[1])) return null;
  return `${m[1]}.${m[2]}`;
}

/**
 * Read the deep-link state from the current URL.
 * Invalid or unknown values are dropped rather than erroring.
 */
export function parseDeepLink(search = window.location.search) {
  const params = new URLSearchParams(search);
  const state = {};

  const section = params.get('section');
  if (section && SECTION_RE.test(section)) {
    const reg = section.split('.')[0];
    if (VALID_REGULATION_CODES.includes(reg)) {
      state.section = section;
      state.reg = reg;
    }
  }

  const reg = params.get('reg');
  if (!state.reg && reg && VALID_REGULATION_CODES.includes(reg)) {
    state.reg = reg;
  }

  const query = params.get('search');
  if (query) state.search = query.slice(0, 200);

  const lang = params.get('lang');
  if (lang && VALID_LANGS.includes(lang)) state.lang = lang;

  return state;
}

/**
 * Build the canonical in-app URL (path + query) for a given view state.
 * Omits defaults so the home view is just "/".
 */
export function buildAppPath({ reg, section, search, lang } = {}) {
  const params = new URLSearchParams();
  if (section) {
    params.set('section', section);
  } else if (reg && reg !== '1910') {
    // 1910 is the default browse view; leave it off to keep "/" clean.
    params.set('reg', reg);
  }
  if (search) params.set('search', search);
  if (lang && lang !== 'en') params.set('lang', lang);
  const qs = params.toString();
  return qs ? `/?${qs}` : '/';
}

/**
 * Build an absolute shareable URL for the current origin.
 */
export function buildShareUrl(state) {
  return `${window.location.origin}${buildAppPath(state)}`;
}

/**
 * Push (or replace) the viewer state into the address bar so the URL is
 * always shareable and back/forward work.
 */
export function syncUrl(state, { replace = false } = {}) {
  const next = buildAppPath(state);
  const current = `${window.location.pathname}${window.location.search}`;
  if (next === current) return;
  if (replace) {
    window.history.replaceState(state, '', next);
  } else {
    window.history.pushState(state, '', next);
  }
}
